import { useState, useCallback } from 'react';

interface WeeklyCapacityWidgetProps {
  capacities?: number[];
  onChange?: (capacities: number[]) => void;
}

const DEFAULT_CAPACITIES = [8, 8, 8, 8, 8, 0, 0]; // Mon-Sun

export function WeeklyCapacityWidget({ 
  capacities = DEFAULT_CAPACITIES,
  onChange 
}: WeeklyCapacityWidgetProps) {
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  const [localCapacities, setLocalCapacities] = useState<number[]>(capacities);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
  const totalCapacity = localCapacities.reduce((sum, cap) => sum + cap, 0);
  
  const handleCapacityChange = useCallback((index: number, value: string) => {
    const numValue = Math.max(0, Math.min(24, parseFloat(value) || 0));
    const newCapacities = [...localCapacities];
    newCapacities[index] = numValue;
    setLocalCapacities(newCapacities);
    onChange?.(newCapacities);
  }, [localCapacities, onChange]);
  
  const incrementCapacity = (index: number) => {
    const newCapacities = [...localCapacities];
    newCapacities[index] = Math.min(24, newCapacities[index] + 0.5);
    setLocalCapacities(newCapacities);
    onChange?.(newCapacities);
  };
  
  const decrementCapacity = (index: number) => {
    const newCapacities = [...localCapacities];
    newCapacities[index] = Math.max(0, newCapacities[index] - 0.5);
    setLocalCapacities(newCapacities);
    onChange?.(newCapacities);
  };
  
  return (
    <div className="bg-white rounded-md shadow-[0px_2px_15px_-3px_rgba(0,0,0,0.07),0px_10px_20px_-2px_rgba(0,0,0,0.04)] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#808080] uppercase tracking-wide">Weekly Capacity</p>
        <span className="text-[12px] font-medium text-[#262626]">
          Total: {totalCapacity}h
        </span>
      </div>
      
      {/* Capacity Grid */}
      <div className="grid grid-cols-7 gap-2">
        {dayNames.map((day, index) => (
          <div 
            key={day}
            className="flex flex-col items-center"
          >
            {/* Day Label */}
            <span className="text-[10px] text-[#808080] mb-2">{shortDays[index]}</span>
            
            {/* Capacity Input */}
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => incrementCapacity(index)}
                className="w-8 h-5 flex items-center justify-center rounded-sm border border-[#e6e6e6] hover:bg-[#f6f6f6] transition-colors text-[10px] text-[#808080]"
              >
                +
              </button>
              
              <input
                type="text"
                value={localCapacities[index]}
                onChange={(e) => handleCapacityChange(index, e.target.value)}
                onFocus={() => setEditingIndex(index)}
                onBlur={() => setEditingIndex(null)}
                className={`w-10 h-8 text-center text-[12px] border rounded-md outline-none transition-colors ${
                  editingIndex === index 
                    ? 'border-[#e30840] bg-white' 
                    : 'border-[#e6e6e6] bg-[#fafafa]'
                }`}
              />
              
              <button
                onClick={() => decrementCapacity(index)}
                className="w-8 h-5 flex items-center justify-center rounded-sm border border-[#e6e6e6] hover:bg-[#f6f6f6] transition-colors text-[10px] text-[#808080]"
              >
                -
              </button>
            </div>
            
            {/* Visual Bar */}
            <div className="w-6 h-[50px] bg-[#f5f5f5] rounded-sm mt-2 flex flex-col justify-end overflow-hidden">
              <div 
                className="w-full bg-[#e30840] transition-all duration-200 rounded-t-sm"
                style={{ 
                  height: `${Math.min((localCapacities[index] / 12) * 100, 100)}%` 
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
